import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonModule } from '@components/buttons/button/button.module';
import { AuthService } from '../../../auth/auth.service';
import {
  createOrderGuideNote,
  formatOrderGuideNoteDate,
  sortOrderGuideNotes,
  type OrderGuideNote,
} from '../order-guide-note.util';

/**
 * Order Guide notes list. Adds/edits notes in memory only; the parent owns persistence on dialog save.
 */
@Component({
  selector: 'app-order-guide-notes-section',
  templateUrl: './order-guide-notes-section.component.html',
  styleUrl: './order-guide-notes-section.component.scss',
  host: { class: 'order-guide-notes-section' },
  imports: [ReactiveFormsModule, ButtonModule, MatButtonModule, MatIconModule, MatTooltipModule],
})
export class OrderGuideNotesSectionComponent {
  private readonly auth = inject(AuthService);

  readonly notes = input.required<OrderGuideNote[]>();
  readonly notesChange = output<OrderGuideNote[]>();

  readonly expanded = signal(true);
  readonly addFormVisible = signal(false);
  /** When set, the form is editing this note instead of adding a new one. */
  readonly editingNote = signal<OrderGuideNote | null>(null);

  readonly newNoteControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.maxLength(4000)],
  });

  /** Rows shown in the list; the note being edited is hidden while in the form. */
  readonly displayNotes = computed(() => {
    const editingId = this.editingNote()?.id;
    if (editingId == null) {
      return this.notes();
    }
    return this.notes().filter((n) => n.id !== editingId);
  });

  private nextPendingId = -1;
  private submitting = false;

  constructor() {
    effect(() => {
      // Seed next pending id below any existing (including previously staged negatives).
      const minId = this.notes().reduce((min, n) => Math.min(min, n.id), 0);
      if (minId < 0) {
        this.nextPendingId = Math.min(this.nextPendingId, minId - 1);
      }
    });
  }

  get isEditing(): boolean {
    return this.editingNote() != null;
  }

  get submitLabel(): string {
    return this.isEditing ? 'Update' : 'Add Note';
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    return this.hasUnsavedDraft();
  }

  /** Typed note text that has not been added or updated yet. */
  hasUnsavedDraft(): boolean {
    const text = String(this.newNoteControl.getRawValue() ?? '').trim();
    if (!text) {
      return false;
    }
    const editing = this.editingNote();
    if (editing) {
      return text !== editing.text.trim();
    }
    return true;
  }

  toggleExpanded(): void {
    this.expanded.update((open) => !open);
  }

  toggleAddForm(event: Event): void {
    event.stopPropagation();
    if (this.addFormVisible() && !this.isEditing) {
      this.closeForm();
      return;
    }
    this.editingNote.set(null);
    this.newNoteControl.reset('');
    this.expanded.set(true);
    this.addFormVisible.set(true);
  }

  startEdit(note: OrderGuideNote): void {
    this.expanded.set(true);
    this.editingNote.set(note);
    this.newNoteControl.setValue(note.text);
    this.addFormVisible.set(true);
  }

  formatDate(createdOn: string): string {
    return formatOrderGuideNoteDate(createdOn);
  }

  submitNote(): void {
    if (this.submitting) return;

    const text = String(this.newNoteControl.getRawValue() ?? '').trim();
    if (!text) {
      this.newNoteControl.markAsTouched();
      return;
    }

    const editing = this.editingNote();
    this.submitting = true;
    try {
      if (editing) {
        if (text === editing.text.trim()) {
          this.closeForm();
          return;
        }
        this.notesChange.emit(
          sortOrderGuideNotes(
            this.notes().map((n) => (n.id === editing.id ? { ...n, text } : n))
          )
        );
        this.closeForm();
        return;
      }

      const note = createOrderGuideNote(text, this.auth, this.nextPendingId--);
      this.notesChange.emit(sortOrderGuideNotes([...this.notes(), note]));
      this.newNoteControl.reset('');
      this.expanded.set(true);
    } finally {
      this.submitting = false;
    }
  }

  removeNote(note: OrderGuideNote): void {
    if (this.editingNote()?.id === note.id) {
      this.closeForm();
    }
    this.notesChange.emit(this.notes().filter((n) => n.id !== note.id));
  }

  private closeForm(): void {
    this.editingNote.set(null);
    this.newNoteControl.reset('');
    this.addFormVisible.set(false);
  }
}
